import React from 'react';
import { Check, X } from 'lucide-react';
import type { DiffHunk } from '../../utils/textDiff';
import Tooltip from '../ui/Tooltip';

interface InlineRewriteDiffProps {
  hunks: DiffHunk[];
  onDecide: (hunkId: string, decision: 'accepted' | 'rejected') => void;
  acceptLabel: string;
  rejectLabel: string;
  className?: string;
}

/**
 * Renders original text with inline change hunks (strike / highlight) and per-hunk accept/reject.
 */
const InlineRewriteDiff: React.FC<InlineRewriteDiffProps> = ({
  hunks,
  onDecide,
  acceptLabel,
  rejectLabel,
  className = '',
}) => {
  return (
    <div className={`text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed ${className}`}>
      {hunks.map((hunk, index) => {
        if (hunk.kind === 'equal') {
          return <span key={`eq-${index}`}>{hunk.text}</span>;
        }

        if (hunk.decision === 'accepted') {
          return <span key={hunk.id}>{hunk.inserted}</span>;
        }
        if (hunk.decision === 'rejected') {
          return <span key={hunk.id}>{hunk.removed}</span>;
        }

        return (
          <span key={hunk.id} className="relative inline align-baseline rounded-sm">
            {hunk.removed ? (
              <span className="bg-red-50 text-red-800/90 line-through decoration-red-400/80">
                {hunk.removed}
              </span>
            ) : null}
            {hunk.inserted ? (
              <span className="bg-emerald-50 text-emerald-900">{hunk.inserted}</span>
            ) : null}
            <span className="ml-0.5 inline-flex align-middle gap-0.5">
              <Tooltip content={rejectLabel}>
                <button
                  type="button"
                  onClick={() => onDecide(hunk.id, 'rejected')}
                  className="inline-flex h-5 w-5 items-center justify-center rounded bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                  aria-label={rejectLabel}
                >
                  <X className="h-3 w-3" strokeWidth={3} />
                </button>
              </Tooltip>
              <Tooltip content={acceptLabel}>
                <button
                  type="button"
                  onClick={() => onDecide(hunk.id, 'accepted')}
                  className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                  aria-label={acceptLabel}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </button>
              </Tooltip>
            </span>
          </span>
        );
      })}
    </div>
  );
};

export default InlineRewriteDiff;
